export interface JobMapJob {
    id: string;
    title: string;
    organization: string;
    address: string;
    precision: 'exact' | 'approximate' | 'unmapped';
    latitude?: number;
    longitude?: number;
    url: string;
    remote: boolean;
}

export interface JobMapPin {
    latitude: number;
    longitude: number;
    precision: 'exact' | 'approximate';
    jobs: JobMapJob[];
}

type LeafletApi = any;
let leafletPromise: Promise<LeafletApi> | null = null;

function cleanText(value: unknown, max = 240): string {
    return typeof value === 'string'
        ? value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max)
        : '';
}

function safeRelativeUrl(value: unknown): string {
    const url = cleanText(value, 1000);
    return url.startsWith('/') && !url.startsWith('//') && !/[\\\u0000-\u001f\u007f]/.test(url) ? url : '';
}

export function normalizeJobMapPayload(value: unknown): JobMapJob[] {
    if (!Array.isArray(value)) return [];
    return value.flatMap((candidate): JobMapJob[] => {
        if (!candidate || typeof candidate !== 'object') return [];
        const raw = candidate as Record<string, unknown>;
        const id = cleanText(raw.id, 120);
        const title = cleanText(raw.title);
        const url = safeRelativeUrl(raw.url);
        if (!id || !title || !url) return [];
        const latitude = Number(raw.latitude);
        const longitude = Number(raw.longitude);
        const validCoordinates = Number.isFinite(latitude) && latitude >= -90 && latitude <= 90
            && Number.isFinite(longitude) && longitude >= -180 && longitude <= 180;
        const requestedPrecision = raw.precision === 'exact' || raw.precision === 'approximate'
            ? raw.precision
            : 'unmapped';
        const remote = raw.remote === true;
        const precision = !remote && validCoordinates ? requestedPrecision : 'unmapped';
        return [{
            id,
            title,
            organization: cleanText(raw.organization),
            address: remote ? 'Remote' : cleanText(raw.address) || 'Location not mapped',
            precision,
            ...(precision !== 'unmapped' ? { latitude, longitude } : {}),
            url,
            remote,
        }];
    });
}

export function groupJobMapPins(jobs: JobMapJob[]): JobMapPin[] {
    const groups = new Map<string, JobMapPin>();
    jobs.forEach((job) => {
        if (job.precision === 'unmapped' || job.latitude === undefined || job.longitude === undefined) return;
        const key = `${job.latitude.toFixed(6)},${job.longitude.toFixed(6)}`;
        const current = groups.get(key);
        if (current) {
            current.jobs.push(job);
            if (job.precision === 'approximate') current.precision = 'approximate';
        }
        else groups.set(key, { latitude: job.latitude, longitude: job.longitude, precision: job.precision, jobs: [job] });
    });
    return Array.from(groups.values());
}

export function loadLeaflet(): Promise<LeafletApi> {
    if (leafletPromise) return leafletPromise;
    leafletPromise = new Promise((resolve, reject) => {
        const existing = (window as any).L;
        if (existing) return resolve(existing);
        if (!document.querySelector('link[data-fj-leaflet-style]')) {
            const style = document.createElement('link');
            style.rel = 'stylesheet';
            style.href = '/css/vendor/leaflet/leaflet.css';
            style.dataset.fjLeafletStyle = 'true';
            document.head.append(style);
        }
        const script = document.createElement('script');
        script.src = '/js/vendor/leaflet/leaflet.js';
        script.async = true;
        script.dataset.fjLeafletScript = 'true';
        const failed = (): void => {
            script.remove();
            reject(new Error('Map library unavailable.'));
        };
        script.addEventListener('load', () => (window as any).L ? resolve((window as any).L) : failed(), { once: true });
        script.addEventListener('error', failed, { once: true });
        document.head.append(script);
    }).catch((error) => {
        // Every consumer, including the editor, can retry a failed library load.
        leafletPromise = null;
        throw error;
    });
    return leafletPromise;
}

export function createOsmLayer(L: LeafletApi): any {
    const StrictReferrerTiles = L.GridLayer.extend({
        createTile(coordinates: { x: number; y: number; z: number }) {
            const tile = document.createElement('img');
            tile.alt = '';
            tile.referrerPolicy = 'strict-origin-when-cross-origin';
            tile.src = `https://tile.openstreetmap.org/${coordinates.z}/${coordinates.x}/${coordinates.y}.png`;
            return tile;
        },
    });
    return new StrictReferrerTiles({ attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors', minZoom: 2, maxZoom: 19 });
}

function popupNode(pin: JobMapPin): HTMLElement {
    const container = document.createElement('div');
    const precision = document.createElement('strong');
    precision.textContent = pin.precision === 'approximate' ? 'Approximate area' : 'Exact location';
    container.append(precision);
    const list = document.createElement('ul');
    pin.jobs.forEach((job) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = job.url;
        link.textContent = job.title;
        item.append(link);
        if (job.organization) item.append(document.createTextNode(` — ${job.organization}`));
        const detail = document.createElement('small');
        detail.textContent = `${job.address} · ${job.precision === 'approximate' ? 'Approximate area' : 'Exact location'}`;
        item.append(document.createElement('br'), detail);
        list.append(item);
    });
    container.append(list);
    return container;
}

export function initializeJobMaps(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>('[data-fj-job-map]').forEach((container) => {
        if (container.dataset.fjJobMapReady === 'true') return;
        container.dataset.fjJobMapReady = 'true';
        const toggle = container.querySelector<HTMLButtonElement>('[data-fj-job-map-toggle]');
        const canvas = container.querySelector<HTMLElement>('[data-fj-job-map-canvas]');
        const status = container.querySelector<HTMLElement>('[data-fj-job-map-status]');
        if (!toggle || !canvas || !status) return;
        let raw: unknown = [];
        try { raw = JSON.parse(container.dataset.fjJobMapPayload ?? '[]'); } catch { raw = []; }
        const pins = groupJobMapPins(normalizeJobMapPayload(raw));
        toggle.addEventListener('click', async () => {
            if (!canvas.hidden) {
                canvas.hidden = true;
                toggle.setAttribute('aria-expanded', 'false');
                toggle.textContent = 'Show jobs on map';
                return;
            }
            canvas.hidden = false;
            toggle.setAttribute('aria-expanded', 'true');
            toggle.textContent = 'Hide job map';
            if (canvas.dataset.fjJobMapInitialized === 'true') return;
            if (pins.length === 0) {
                status.textContent = 'These jobs do not have confirmed map locations. Use the job list below.';
                return;
            }
            status.textContent = 'Loading the job map…';
            try {
                const L = await loadLeaflet();
                const map = L.map(canvas, { scrollWheelZoom: false });
                createOsmLayer(L).addTo(map);
                const bounds: Array<[number, number]> = [];
                pins.forEach((pin) => {
                    bounds.push([pin.latitude, pin.longitude]);
                    L.marker([pin.latitude, pin.longitude]).addTo(map).bindPopup(popupNode(pin));
                });
                map.fitBounds(bounds, { padding: [24, 24], maxZoom: 12 });
                canvas.dataset.fjJobMapInitialized = 'true';
                status.textContent = `${pins.length} ${pins.length === 1 ? 'location' : 'locations'} shown. Approximate pins represent an area center.`;
            } catch {
                canvas.hidden = true;
                toggle.setAttribute('aria-expanded', 'false');
                toggle.textContent = 'Try job map again';
                status.textContent = 'The map could not load. Every job remains available in the list below.';
            }
        });
    });
}
